import {
  collectComponentRemovalCandidates,
  collectInvisibleKeys,
  componentSourcesEqual,
  functionIdFromPath,
  hasUnanalyzableSourceFileReferences,
  isCollectableSourceFile,
  MAX_SOURCE_FILES_PER_GC_PASS,
  type ComponentSource,
  type SourceFilesState,
} from '@packages/systems/code-libraries/core';
import {engineConfig, type Dispatch} from '@packages/systems/flux';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {telemetry} from '@packages/systems/designer-telemetry';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {notificationAdded} from '@packages/systems/notifications/client';
import NotificationRecord from '@packages/systems/core/records/NotificationRecord';
import {createSelector} from 'reselect';

import {selectDesignerAbility} from '@packages/systems/designer-abilities';
// eslint-disable-next-line webflow/module-top-level-imports
import {selectIsAgentTurnInProgress} from '@packages/systems/ai/assistant-next/frontend/selectors';

import {selectSiteCodeComponentSources} from '../selectors';
import {
  selectActiveSourceFileKey,
  selectComponentMapHydrationEpoch,
  selectDirtySourceFileKeys,
  selectIsPageBranch,
  selectSandboxJobStatus,
  selectSourceFiles,
} from './CodeLibrariesSlice';
import {vccUnusedSourceFilesCollected} from './actionCreators';

// Re-checked from scratch when it fires, so a component that is only briefly
// absent -- a map swap, or an agent writing a module before its importer --
// never costs a file.
const SETTLE_MS = 2000;

export type SourceFileCollectionState = {
  componentSources: readonly ComponentSource[];
  files: SourceFilesState;
  activeKey: string | null;
  dirtyKeys: ReadonlySet<string>;
  mapEpoch: number;
  /**
   * The same three locks the tree gates every file mutation on
   * (`canManageFiles`, `SourceFileTree.tsx`). Collecting is a file mutation, so
   * the engine's authority must not exceed the button's: without permission a
   * viewer's session would delete; mid-agent-turn the collector writes into the
   * store the agent is writing into; mid-rebuild it races the sandbox write the
   * build is reading.
   */
  canManageFiles: boolean;
};

const canRunOnThisPartition = (isPageBranch: boolean): boolean => !isPageBranch;

/**
 * A primitive key in the style of `selectAbandonableCreatorKey` is not possible
 * here: any honest key would have to run the whole import walk inside the
 * selector, on every staged canvas edit. So this stays a cheap object and the
 * transition detection lives in the engine body.
 */
export const selectSourceFileCollectionState = createSelector(
  selectSiteCodeComponentSources,
  selectSourceFiles,
  selectActiveSourceFileKey,
  selectDirtySourceFileKeys,
  selectComponentMapHydrationEpoch,
  // Not `selectDesignerAbility('canEditCodeComponents')` directly: that call
  // resolves the ability's selector function the instant this module loads,
  // via `createSelector`'s argument list. `WebflowFlux.ts` is the single most
  // widely imported file in designer-flux, and many unrelated test files mock
  // `@packages/systems/designer-abilities` with a bare `jest.fn()` (no
  // `mockReturnValue`) since they don't care about ability gating -- that
  // returns `undefined` in place of a selector, and `createSelector` throws at
  // module-load time for every test that transitively imports this file
  // through the barrel, not just ones that touch source-file GC. Wrapping
  // defers the lookup to select-time, when it only ever runs against the real
  // implementation.
  (state: DesignerStores) =>
    selectDesignerAbility('canEditCodeComponents')(state),
  selectIsAgentTurnInProgress,
  selectSandboxJobStatus,
  selectIsPageBranch,
  (
    componentSources,
    files,
    activeKey,
    dirtyKeys,
    mapEpoch,
    canEditCodeComponents,
    isAgentTurnInProgress,
    jobStatus,
    isPageBranch
  ) => ({
    componentSources,
    files,
    activeKey,
    dirtyKeys,
    mapEpoch,
    canManageFiles:
      canEditCodeComponents &&
      !isAgentTurnInProgress &&
      jobStatus === 'IDLE' &&
      canRunOnThisPartition(isPageBranch),
  })
);

// `dirtyKeys` is reference-stable between keystrokes (its selector memoizes on
// a key signature), so reference equality is enough for both sets.
const sameInputs = (
  a: SourceFileCollectionState,
  b: SourceFileCollectionState
): boolean =>
  a.files === b.files &&
  a.activeKey === b.activeKey &&
  a.dirtyKeys === b.dirtyKeys &&
  a.mapEpoch === b.mapEpoch &&
  a.canManageFiles === b.canManageFiles &&
  componentSourcesEqual(a.componentSources, b.componentSources);

const protectedKeysFor = (
  state: SourceFileCollectionState
): ReadonlySet<string> => {
  const protectedKeys = new Set(state.dirtyKeys);
  if (state.activeKey) protectedKeys.add(state.activeKey);
  return protectedKeys;
};

// A function is known by its invocation id, not its path. A plain module
// keeps enough of its path to tell `pricing/Card.tsx` from `hero/Card.tsx` in
// the toast -- the basename alone collides.
const describeCollected = (name: string): string => {
  const functionId = functionIdFromPath(name);
  if (functionId !== null) return functionId;
  const slashIndex = name.lastIndexOf('/');
  return slashIndex <= 0
    ? name
    : name.slice(name.lastIndexOf('/', slashIndex - 1) + 1);
};

const collectionNotification = (names: readonly string[]) => {
  const quoted = names.map((name) => `“${name}”`).join(', ');
  return NotificationRecord({
    type: 'info',
    title: names.length === 1 ? 'Unused file removed' : 'Unused files removed',
    message: `Nothing imports ${quoted} anymore.`,
    delay: 6000,
    icon: true,
    dismissIcon: true,
  });
};

/**
 * Retires source files that no component's tree would show any more (EXT-1962).
 *
 * Driven by a transition, not a state: a file goes only after it has been seen
 * visible and then goes invisible. A file never seen visible is mid-authoring
 * and is left alone -- which is also what makes boot, catchup, undo and rename
 * safe without special cases, since each presents a never-seen key.
 *
 * Dropping the keys is the whole mechanism: the multiplayer diff of
 * `SourceFilesStore` produces the remove op, `SourceFilesSyncEngine` derives
 * the REST tombstone, and `projectFilesystem` flags the sandbox path
 * `PENDING_DELETE` for `RemoteSandboxEngine` to remove.
 */
export const UnusedSourceFileCollectorEngine = (
  dispatch: Dispatch<
    | ReturnType<typeof vccUnusedSourceFilesCollected>
    | ReturnType<typeof notificationAdded>
  >
) => {
  let observedVisible = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let latest: SourceFileCollectionState | null = null;
  let previous: SourceFileCollectionState | null = null;
  let armed = new Set<string>();
  let lastMapEpoch: number | null = null;

  // Read per pass without logging exposure so the kill switch also closes
  // during the settle window.
  const isEnabled = () =>
    getFeatureFlagOutsideOfReact(FEATURE_FLAGS.SOURCE_FILE_GC, {
      shouldLogExposure: false,
    });

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  // Shared by the arming pass and the settle re-check so the two cannot drift.
  const evaluate = (state: SourceFileCollectionState) => {
    // Not the tree's alwaysInclude: the tree also protects pinnedKeys (a file
    // just created or renamed). Both mint a key never observed visible, so the
    // transition rule already protects them without this set needing to match.
    const protectedKeys = protectedKeysFor(state);
    // Seeding deliberately ignores protection. A file that is visible only
    // because the editor holds it open was never visible on its own, and
    // seeding it would let protection ARM collection rather than defer it --
    // deleting the file the tree just created, or just renamed while importers
    // still name the old path, the moment the user navigates away.
    const invisibleUnprotected = collectInvisibleKeys(
      state.componentSources,
      state.files
    );
    const invisible =
      protectedKeys.size === 0
        ? invisibleUnprotected
        : collectInvisibleKeys(
            state.componentSources,
            state.files,
            protectedKeys
          );
    const invisibleCollectable = new Set<string>();
    const visible = new Set<string>();
    for (const [key, file] of Object.entries(state.files)) {
      if (!invisibleUnprotected.has(key)) visible.add(key);
      if (invisible.has(key) && isCollectableSourceFile(file)) {
        invisibleCollectable.add(key);
      }
    }
    return {invisibleCollectable, visible};
  };

  const collect = () => {
    timer = null;
    // Re-read rather than trusting the pass that armed the timer: without this
    // the kill switch has a SETTLE_MS blind window.
    if (latest === null || !latest.canManageFiles || !isEnabled()) return;
    // Recomputed rather than trusting what armed the timer, so a file whose
    // importer came back in the meantime is not collected.
    const {invisibleCollectable} = evaluate(latest);
    const keys = [...invisibleCollectable].filter((key) =>
      observedVisible.has(key)
    );
    if (keys.length === 0) return;
    if (keys.length > MAX_SOURCE_FILES_PER_GC_PASS) {
      telemetry.sendEvent('datadog::sourceFileGcRefused', {
        reason: 'over_cap',
        candidates: keys.length,
      });
      return;
    }
    const names = keys.map((key) => {
      const file = latest?.files[key];
      return file ? describeCollected(file.name) : key;
    });
    for (const key of keys) observedVisible.delete(key);
    // The only signal that this ran. Without it a healthy ramp and one eating
    // files look identical from the outside.
    telemetry.sendEvent('datadog::sourceFileGcCollected', {
      count: keys.length,
      names,
    });
    dispatch(vccUnusedSourceFilesCollected({keys}));
    dispatch(notificationAdded(collectionNotification(names)));
  };

  return (state: SourceFileCollectionState) => {
    if (!isEnabled()) {
      clearTimer();
      observedVisible = new Set();
      armed = new Set();
      return;
    }

    // A wholesale reload rebuilds the component map from its payload alone
    // while source files survive untouched, so every component missing from a
    // short or stale payload looks exactly like one the user deleted. Drop the
    // baseline: missing a collection that races a reload is recoverable,
    // deleting a file nobody orphaned is not. Same trade
    // `SourceFilesSyncEngine` makes before deriving tombstones.
    //
    // The pass then continues rather than returning, so this one re-seeds from
    // the reload's own answer. Arming cannot happen on it -- the set it would
    // have to match against was just emptied.
    const previousState = previous;
    const mapEpochChanged = state.mapEpoch !== lastMapEpoch;
    if (mapEpochChanged) {
      lastMapEpoch = state.mapEpoch;
      observedVisible = new Set();
      armed = new Set();
      clearTimer();
    }
    // The selector rebuilds on every staged canvas edit, but the walk only
    // reads component id/code, the files object and the protected keys. Without
    // this the whole site's imports are re-parsed for edits that cannot change
    // the answer.
    if (previous !== null && sameInputs(previous, state)) return;
    previous = state;
    latest = state;

    if (!mapEpochChanged && state.canManageFiles && previousState !== null) {
      const currentIds = new Set(state.componentSources.map(({id}) => id));
      const removed = previousState.componentSources.filter(
        ({id}) => !currentIds.has(id)
      );
      const removedComponent = removed[0];
      if (removed.length === 1 && removedComponent !== undefined) {
        const result = collectComponentRemovalCandidates(
          previousState.componentSources,
          removedComponent.id,
          previousState.files,
          protectedKeysFor(previousState)
        );
        if (result.status === 'refused') {
          telemetry.sendEvent('datadog::sourceFileGcRefused', {
            reason: result.reason,
            ...(result.reason === 'over_cap'
              ? {candidates: result.candidates}
              : {}),
          });
        } else if (
          result.keys.length > 0 &&
          result.keys.every((key) => state.files[key] === undefined)
        ) {
          const names = result.keys.map((key) => {
            const file = previousState.files[key];
            return file ? describeCollected(file.name) : key;
          });
          telemetry.sendEvent('datadog::sourceFileGcCollected', {
            count: result.keys.length,
            names,
          });
          dispatch(notificationAdded(collectionNotification(names)));
        }
      }
    }

    // An empty map marks every file invisible. That is never a legitimate
    // basis for deletion, and it is what a partial reload looks like.
    if (
      !state.canManageFiles ||
      state.componentSources.length === 0 ||
      hasUnanalyzableSourceFileReferences(state.componentSources, state.files)
    ) {
      clearTimer();
      return;
    }

    const {invisibleCollectable, visible} = evaluate(state);

    // Forget keys that are gone from the store entirely, so a delete-then-
    // recreate of the same path starts over as never-observed.
    for (const key of observedVisible) {
      if (state.files[key] === undefined) observedVisible.delete(key);
    }

    const armedNow = new Set<string>();
    for (const key of invisibleCollectable) {
      if (observedVisible.has(key)) armedNow.add(key);
    }
    for (const key of visible) observedVisible.add(key);

    // Restart the window when a key arms that was not already armed, so every
    // file gets a full settle rather than whatever is left of one an earlier
    // file started. Keys already armed do not restart it, or a site under
    // continuous editing would never reach a collection.
    const hasNewlyArmed = [...armedNow].some((key) => !armed.has(key));
    armed = armedNow;

    if (armed.size === 0) {
      clearTimer();
      return;
    }
    if (hasNewlyArmed || timer === null) {
      clearTimer();
      timer = setTimeout(collect, SETTLE_MS);
    }
  };
};

export const unusedSourceFileCollectorEngineConfig = engineConfig<
  DesignerStores,
  | ReturnType<typeof vccUnusedSourceFilesCollected>
  | ReturnType<typeof notificationAdded>,
  SourceFileCollectionState
>(UnusedSourceFileCollectorEngine, selectSourceFileCollectionState);
